import { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'
import { refresh } from './store'

/**
 * SINCRONIZACIÓN EN TIEMPO REAL
 * ------------------------------------------------------------------
 * Escucha los cambios de las tablas del espacio de trabajo y refresca la
 * caché del store. No modifica ninguna política RLS: el filtro por
 * `organization_id` que se aplica aquí es solo para no recibir eventos
 * ajenos; la seguridad real sigue estando en la base de datos.
 */

/** Tablas ya añadidas a la publicación `supabase_realtime`. */
const TABLES = [
  'leads',
  'lead_notes',
  'lead_status_history',
  'forms',
  'landing_pages',
  'form_submissions',
] as const

export type RealtimeStatus = 'sincronizado' | 'actualizando' | 'sin_conexion'

const DEBOUNCE_MS = 300

let channel: RealtimeChannel | null = null
let channelOrganizationId: string | null = null
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let status: RealtimeStatus = 'sin_conexion'

const listeners = new Set<() => void>()

function setStatus(next: RealtimeStatus): void {
  if (status === next) return
  status = next
  listeners.forEach((listener) => listener())
}

/** Estado de conexión actual, consultable desde la interfaz. */
export function realtimeStatus(): RealtimeStatus {
  return status
}

/** Se notifica en cada cambio de estado (compatible con useSyncExternalStore). */
export function subscribeRealtimeStatus(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Agrupa varios eventos seguidos en un único `refresh()` para no recargar
 * el espacio de trabajo una vez por fila modificada.
 */
function scheduleRefresh(): void {
  setStatus('actualizando')
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    debounceTimer = null
    try {
      await refresh()
      setStatus('sincronizado')
    } catch (err) {
      console.error('[AVA] Realtime: fallo al refrescar el espacio de trabajo:', err)
      setStatus('sin_conexion')
    }
  }, DEBOUNCE_MS)
}

/**
 * Abre el canal de la organización indicada. Si ya existe uno para esa misma
 * organización no hace nada; si es de otra, lo cierra antes de abrir el nuevo.
 */
export function startRealtime(organizationId: string): void {
  if (!organizationId) return
  if (channel && channelOrganizationId === organizationId) return
  stopRealtime()

  channelOrganizationId = organizationId
  const next = supabase.channel(`workspace:${organizationId}`)

  for (const table of TABLES) {
    next.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table,
        filter: `organization_id=eq.${organizationId}`,
      },
      () => scheduleRefresh(),
    )
  }

  next.subscribe((state) => {
    if (state === 'SUBSCRIBED') setStatus('sincronizado')
    else if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT' || state === 'CLOSED')
      setStatus('sin_conexion')
  })

  channel = next
}

/** Cierra el canal activo y deja el estado sin conexión. */
export function stopRealtime(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  if (channel) {
    supabase.removeChannel(channel)
    channel = null
  }
  channelOrganizationId = null
  setStatus('sin_conexion')
}

/** Refresco manual de respaldo, con el mismo indicador de estado. */
export async function manualRefresh(): Promise<void> {
  setStatus('actualizando')
  try {
    await refresh()
    setStatus(channel ? 'sincronizado' : 'sin_conexion')
  } catch (err) {
    console.error('[AVA] Realtime: fallo en la actualización manual:', err)
    setStatus('sin_conexion')
  }
}
